/**
 * Finds specified Object by key and value. Returns the first search result
 * undefined if Object can't be found.
 * @param entireObj Object to be searched.
 * @param keyToFind Key of searched Object.
 * @param valToFind Value of specified key of searched Object.
 */
export function findNestedObjRecursive(
  obj: any,
  keyToFind: any,
  valToFind: any,
) {
  if (obj && typeof obj === 'object') {
    if (
      Object.prototype.hasOwnProperty.call(obj, keyToFind) &&
      obj[keyToFind] === valToFind
    ) {
      return obj;
    }
    for (const k in obj) {
      const result: any = findNestedObjRecursive(obj[k], keyToFind, valToFind);
      if (result) {
        return result;
      }
    }
  }
  return undefined;
}

export function isObject(item: any): boolean {
  return item && typeof item === 'object' && !Array.isArray(item);
}

export function isNotUndefined<T>(value: T | undefined): value is T {
  return typeof value !== 'undefined';
}

export function isNotNullable<T>(value: T): value is NonNullable<T> {
  return isNotUndefined(value) && value !== null;
}

type Objectable = {
  [key: string]: any;
};

export function shallowEqualObjects(
  objA: Objectable,
  objB: Objectable,
): boolean {
  if (objA === objB) {
    return true;
  }
  if (!objA || !objB) {
    return false;
  }
  const aKeys = Object.keys(objA);
  const bKeys = Object.keys(objB);
  const aKeysLen = aKeys.length;
  const bKeysLen = bKeys.length;

  if (aKeysLen !== bKeysLen) {
    return false;
  }
  for (let i = 0; i < aKeysLen; i++) {
    const key = aKeys[i];
    if (objA[key] !== objB[key]) {
      return false;
    }
  }
  return true;
}

export function deepEqualObjects(objA: Objectable, objB: Objectable): boolean {
  if (objA === objB) {
    return true; // if both objA and objB are null or undefined and exactly the same
  } else if (!(objA instanceof Object) || !(objB instanceof Object)) {
    return false; // if they are not strictly equal, they both need to be Objects
  } else if (objA.constructor !== objB.constructor) {
    // they must have the exact same prototype chain, the closest we can do is
    // test their constructor.
    return false;
  } else {
    for (const key in objA) {
      // eslint-disable-next-line no-prototype-builtins
      if (!objA.hasOwnProperty(key)) {
        continue; // other properties were tested using objA.constructor === y.constructor
      }
      // eslint-disable-next-line no-prototype-builtins
      if (!objB.hasOwnProperty(key)) {
        return false; // allows to compare objA[ key ] and objB[ key ] when set to undefined
      }
      if (objA[key] === objB[key]) {
        continue; // if they have the same strict value or identity then they are equal
      }
      if (typeof objA[key] !== 'object') {
        return false; // Numbers, Strings, Functions, Booleans must be strictly equal
      }

      if (!deepEqualObjects(objA[key], objB[key])) {
        return false;
      }
    }
    for (const key in objB) {
      // eslint-disable-next-line no-prototype-builtins
      if (objB.hasOwnProperty(key) && !objA.hasOwnProperty(key)) {
        return false;
      }
    }
    return true;
  }
}
